import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

export function EmailConfirmationHandler() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your confirmation...');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        console.log('Handling email confirmation');
        // Get the hash fragment from the URL
        const hash = location.hash;
        
        if (!hash) {
          console.error('No hash fragment found in URL');
          setStatus('error');
          setMessage('No confirmation token found in the URL.');
          return;
        }

        console.log('Hash fragment found:', hash);
        // Parse the hash to get access_token and other params
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        if (!accessToken || !refreshToken) {
          console.error('Missing tokens in hash params');
          setStatus('error');
          setMessage('Invalid confirmation link. Missing required parameters.');
          return;
        }

        console.log('Tokens found, type:', type);
        if (type === 'signup' || type === 'recovery') {
          // Set the session manually using the tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Error setting session:', error);
            throw error;
          }

          if (data?.user) {
            console.log('Session set successfully, user:', data.user.id);
            setStatus('success');
            setMessage('Your email has been confirmed successfully!');
            
            // Redirect to home page after a short delay
            setTimeout(() => {
              navigate('/');
            }, 2000);
          } else {
            console.error('No user data returned after setting session');
            throw new Error('Failed to set session');
          }
        } else {
          console.error('Unknown confirmation type:', type);
          setStatus('error');
          setMessage('Unknown confirmation type.');
        }
      } catch (error) {
        console.error('Error handling email confirmation:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'An error occurred during confirmation');
      }
    };

    handleEmailConfirmation();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirming Your Email</h1>
            <p className="text-gray-600">Please wait while we confirm your email address...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Confirmed!</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-gray-600">You'll be redirected to the dashboard automatically.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirmation Failed</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}